import { useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { router } from 'expo-router';
import * as Application from 'expo-application';
import { observer } from 'mobx-react';

import CollapsibleDebugSection from '@/components/CollapsibleDebugSection';
import DebugLocalAudioPlayer from '@/components/DebugLocalAudioPlayer';
import DebugLocalVideoPlayer from '@/components/DebugLocalVideoPlayer';
import DefaultBackground from '@/components/DefaultBackground';
import DownloadVideoDemo from '@/components/DownloadVideoDemo';
import TopbarLogo from '@/components/TopbarLogo';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { useScale } from '@/hooks/useScale';
import { useStores } from '@/stores/StoreProvider';
import { getAppBuildNumber, getAppVersion } from '@/utils/appVersion';
import {
  isMobileWebDevice,
  mobileGateSource,
} from '@/utils/isMobileWebDevice';
import { createMMKV } from 'react-native-mmkv';
import * as Sentry from '@sentry/react-native';
import { VIDEO_DEBUG_VARIATIONS } from '@/config/videoDebugVariations';

const storage = createMMKV();

function DebugScreen() {
  const styles = useDebugScreenStyles();
  const { userStore } = useStores();
  const currentUser = userStore.currentUser;
  const modifications = currentUser?.modifications ?? [];
  const [isTogglingLogs, setIsTogglingLogs] = useState(false);

  const toggleStreamTvLogs = async () => {
    if (!currentUser) return;
    const next = !currentUser.streamTvLogs;
    setIsTogglingLogs(true);
    try {
      await currentUser.setStreamTvLogs(next);
      console.log(`[Debug] STREAM_TV_LOGS set to ${next}`);
    } catch (error) {
      console.warn('[Debug] Failed to update STREAM_TV_LOGS:', error);
      Alert.alert(
        'Could not update setting',
        'Something went wrong while updating log streaming.',
      );
    } finally {
      setIsTogglingLogs(false);
    }
  };

  const testMmkv = () => {
    const testKey = 'explore_test_key';
    const testValue = `Hello MMKV! Timestamp: ${Date.now()}`;
    storage.set(testKey, testValue);
    const retrievedValue = storage.getString(testKey);
    console.log('[Explore] MMKV test value:', retrievedValue);
  };

  const getBundleId = () => {
    if (Platform.OS === 'web') {
      return 'web (no bundle id)';
    }
    return Application.applicationId ?? 'unknown';
  };

  const bundleId = getBundleId();
  const version = getAppVersion() ?? 'unknown';
  const buildNumber = getAppBuildNumber() ?? 'unknown';

  return (
    <ThemedView style={styles.container}>
      <DefaultBackground />

      {/* Top bar */}
      <View style={styles.topBar}>
        <Pressable
          style={({ focused }) => [
            styles.backButton,
            focused && styles.backButtonFocused,
          ]}
          onPress={() => router.back()}
        >
          <ThemedText style={styles.backButtonText}>Back</ThemedText>
        </Pressable>
        <View style={styles.topBarSpacer} />
        <TopbarLogo />
        <View style={styles.topBarSpacer} />
        <View style={styles.backButtonPlaceholder} />
      </View>

      <ThemedText style={styles.title}>Debug</ThemedText>

      <ScrollView
        scrollEventThrottle={16}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>App Info</ThemedText>
          <ThemedText style={styles.infoText}>Bundle ID: {bundleId}</ThemedText>
          <ThemedText style={styles.infoText}>
            Build Number: {buildNumber}
          </ThemedText>
          <ThemedText style={styles.infoText}>Version: {version}</ThemedText>
          <ThemedText style={styles.infoText}>
            __DEV__: {`${__DEV__}`}
          </ThemedText>
          {Platform.OS === 'web' && (
            // Only reachable when the gate is open, so this always reads
            // "off" — but it says WHY, which is the part you can't otherwise
            // see. Force it with ?mobileGate=on|off|auto.
            <ThemedText style={styles.infoText}>
              Mobile gate: {isMobileWebDevice ? 'on' : 'off'} ({mobileGateSource}
              )
            </ThemedText>
          )}
        </View>

        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Tools</ThemedText>
          <Pressable
            onPress={() => router.push('/debug/file-system')}
            style={({ focused }) => [
              styles.button,
              focused && styles.buttonFocused,
            ]}
          >
            <ThemedText style={styles.buttonText}>File System</ThemedText>
          </Pressable>
          <Pressable
            onPress={() => router.push('/debug/tv-native-events')}
            style={({ focused }) => [
              styles.button,
              focused && styles.buttonFocused,
            ]}
          >
            <ThemedText style={styles.buttonText}>
              Debug TV Native Events
            </ThemedText>
          </Pressable>
        </View>

        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>
            User Modifications
          </ThemedText>
          {modifications.length === 0 ? (
            <ThemedText style={styles.mutedText}>None</ThemedText>
          ) : (
            modifications.map((um) => (
              <View key={um.id} style={styles.modificationItem}>
                <ThemedText style={styles.modificationName}>
                  {um.modification.name} ({um.modification.unique_name})
                </ThemedText>
                <ThemedText style={styles.mutedText}>
                  {um.modification.workout_types.length === 0
                    ? 'No allowed workout types'
                    : `${um.modification.workout_types.length} allowed workout type(s):`}
                </ThemedText>
                {um.modification.workout_types.map((wt, index) => (
                  <ThemedText
                    key={`${wt.workout_template_type}-${wt.fitness_level}-${index}`}
                    style={styles.mutedText}
                  >
                    • {wt.workout_template_type} — {wt.fitness_level}
                  </ThemedText>
                ))}
              </View>
            ))
          )}
        </View>

        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>
            Video Debug Variations
          </ThemedText>
          <View style={styles.variationGrid}>
            {VIDEO_DEBUG_VARIATIONS.map((v) => (
              <Pressable
                key={v.id}
                onPress={() =>
                  router.push({
                    pathname: '/debug/video-variations/[variation]',
                    params: { variation: v.id },
                  })
                }
                style={({ focused }) => [
                  styles.button,
                  focused && styles.buttonFocused,
                ]}
              >
                <ThemedText style={styles.buttonText}>{v.label}</ThemedText>
              </Pressable>
            ))}
          </View>
        </View>

        <CollapsibleDebugSection title="Download Video Demo">
          <DownloadVideoDemo />
        </CollapsibleDebugSection>

        <CollapsibleDebugSection title="Local Audio">
          <DebugLocalAudioPlayer />
        </CollapsibleDebugSection>

        <CollapsibleDebugSection title="Local Video">
          <DebugLocalVideoPlayer />
        </CollapsibleDebugSection>

        <View style={styles.section}>
          <Pressable
            onPress={testMmkv}
            style={({ focused }) => [
              styles.button,
              focused && styles.buttonFocused,
            ]}
          >
            <ThemedText style={styles.buttonText}>Test MMKV</ThemedText>
          </Pressable>
          <Pressable
            onPress={() => {
              Alert.alert(
                'Test Alert',
                'This is a test alert to verify that alerts are working correctly.',

                [{ text: 'OK', onPress: () => console.log('Alert dismissed') }],
              );
            }}
            style={({ focused }) => [
              styles.button,
              focused && styles.buttonFocused,
            ]}
          >
            <ThemedText style={styles.buttonText}>Test Alert</ThemedText>
          </Pressable>
        </View>

        <View style={styles.section}>
          <Pressable
            onPress={() => {
              Sentry.captureException(new Error('Sentry test error'));
            }}
            style={({ focused }) => [
              styles.button,
              focused && styles.buttonFocused,
            ]}
          >
            <ThemedText style={styles.buttonText}>
              Test Sentry Exception
            </ThemedText>
          </Pressable>

          <Pressable
            onPress={() => {
              try {
                console.log('This is a log from JavaScript console.log');
                Sentry.logger.info(
                  'This is an info log from the Sentry logger',
                );

                alert(
                  'Logs sent to Sentry! Check your Sentry dashboard to see them.',
                );
              } catch (error) {
                console.error('Error sending logs to Sentry:', error);
                alert(
                  'Failed to send logs to Sentry. Check console for details.',
                );
              }
            }}
            style={({ focused }) => [
              styles.button,
              focused && styles.buttonFocused,
            ]}
          >
            <ThemedText style={styles.buttonText}>Test Sentry Logs</ThemedText>
          </Pressable>

          {currentUser ? (
            <Pressable
              disabled={isTogglingLogs}
              onPress={toggleStreamTvLogs}
              style={({ focused }) => [
                styles.button,
                focused && styles.buttonFocused,
                isTogglingLogs && styles.buttonDisabled,
              ]}
            >
              {isTogglingLogs ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <ThemedText style={styles.buttonText}>
                  Stream TV logs: {currentUser.streamTvLogs ? 'On' : 'Off'}
                </ThemedText>
              )}
            </Pressable>
          ) : null}
        </View>
      </ScrollView>
    </ThemedView>
  );
}

const useDebugScreenStyles = function () {
  const scale = useScale();
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    topBar: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 24 * scale,
      paddingHorizontal: 24 * scale,
    },
    topBarSpacer: {
      flex: 1,
    },
    backButton: {
      paddingVertical: 8 * scale,
      paddingHorizontal: 20 * scale,
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: 20 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    backButtonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    backButtonPlaceholder: {
      width: 80 * scale,
    },
    backButtonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    title: {
      color: '#fff',
      fontSize: 28 * scale,
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: 16 * scale,
    },
    scrollContent: {
      paddingHorizontal: 40 * scale,
      paddingBottom: 40 * scale,
      gap: 16 * scale,
    },
    section: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: 12 * scale,
      padding: 20 * scale,
      gap: 10 * scale,
    },
    sectionTitle: {
      color: '#fff',
      fontSize: 18 * scale,
      fontWeight: '700',
    },
    row: {
      flexDirection: 'row',
      gap: 12 * scale,
      alignItems: 'center',
    },
    variationGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12 * scale,
      alignItems: 'center',
    },
    mutedText: {
      color: 'rgba(255,255,255,0.7)',
    },
    infoText: {
      color: '#fff',
    },
    button: {
      paddingVertical: 10 * scale,
      paddingHorizontal: 18 * scale,
      borderRadius: 10 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
      alignSelf: 'flex-start',
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
    buttonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    buttonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    buttonDisabled: {
      opacity: 0.4,
    },
    videoContainer: {
      width: '100%',
    },
    video: {
      width: '100%',
      height: 220 * scale,
    },
    modificationItem: {
      gap: 4 * scale,
      paddingVertical: 6 * scale,
    },
    modificationName: {
      color: '#fff',
      fontSize: 15 * scale,
      fontWeight: '600',
    },
  });
};

export default observer(DebugScreen);
